import {
	Component,
	computed,
	inject,
	type OnInit,
	signal,
	ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule, type Popover } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import type { MenuItem } from 'primeng/api';
import { MsalService } from '@azure/msal-angular';
import { ToastService } from '../../services/toast.service';
import {
	PartCodingQueueService,
	type HistoryWriteOutcome
} from '../../services/part-coding-queue.service';
import type {
	PartCodingApproval,
	PartCodingQueueLine,
	PartCodingStatus
} from '../../models/part-coding.model';
import { PART_CODING_STATUSES } from '../part-coding/part-coding-helpers';
import {
	buildAdminQueueCsv,
	formatSubmitted,
	formatSubmittedDate,
	formatSubmittedTime,
	lineMatchesQuery,
	readApprovalUpload,
	submittedDay,
	submittedDayLabel,
	type UploadError
} from './part-coding-admin-helpers';

interface FilterOption {
	label: string;
	value: string;
}

@Component({
	selector: 'app-part-coding-admin',
	standalone: true,
	imports: [
		FormsModule,
		TableModule,
		ButtonModule,
		DialogModule,
		InputTextModule,
		PopoverModule,
		SelectModule,
		SplitButtonModule,
		TagModule,
		TooltipModule
	],
	templateUrl: './part-coding-admin.component.html',
	styleUrl: './part-coding-admin.component.sass'
})
export class PartCodingAdminComponent implements OnInit {
	private readonly toastService = inject(ToastService);
	private readonly queue = inject(PartCodingQueueService);
	private readonly msal = inject(MsalService);

	@ViewChild('reasonPopover') private reasonPopover?: Popover;

	public readonly statuses: PartCodingStatus[] = [...PART_CODING_STATUSES];
	public readonly formatSubmitted = formatSubmitted;
	public readonly formatSubmittedDate = formatSubmittedDate;
	public readonly formatSubmittedTime = formatSubmittedTime;

	public readonly signedInUser = signal(this.accountName());
	public readonly customerFilter = signal<string | null>(null);
	public readonly isrFilter = signal<string | null>(null);
	public readonly dateFilter = signal<string | null>(null);
	public readonly batchFilter = signal<string | null>(null);
	public readonly searchQuery = signal('');
	public readonly statusFilter = signal<ReadonlySet<PartCodingStatus>>(
		new Set()
	);
	public readonly selectedIds = signal<ReadonlySet<string>>(new Set());

	public readonly activeLine = signal<PartCodingQueueLine | null>(null);
	public readonly uploadFileName = signal('');
	public readonly uploadErrors = signal<readonly UploadError[]>([]);
	public readonly uploadDialogVisible = signal(false);

	public readonly exportItems: MenuItem[] = [
		{
			label: 'Export all',
			command: (): void => {
				this.exportQueue('all');
			}
		},
		{
			label: 'Export selected',
			command: (): void => {
				this.exportQueue('selected');
			}
		}
	];

	public readonly customerOptions = computed(() =>
		this.optionsFrom(this.queue.lines().map((line) => line.customer))
	);
	public readonly isrOptions = computed(() =>
		this.optionsFrom(this.queue.lines().map((line) => line.isr))
	);
	public readonly dateOptions = computed<FilterOption[]>(() =>
		[
			...new Set(
				this.queue.lines().map((line) => submittedDay(line.submittedAt))
			)
		]
			.sort((a, b) => b.localeCompare(a))
			.map((day) => ({ label: submittedDayLabel(day), value: day }))
	);
	public readonly batchOptions = computed<FilterOption[]>(() =>
		[...new Set(this.queue.lines().map((line) => line.batchId))]
			.filter((id): id is number => id != null)
			.sort((a, b) => b - a)
			.map((id) => ({ label: `Batch ${id}`, value: String(id) }))
	);

	// Customer / ISR / date / search narrow the queue first so the status pill
	// counts describe what is left to filter.
	public readonly baseLines = computed(() => {
		const customer = this.customerFilter();
		const isr = this.isrFilter();
		const day = this.dateFilter();
		const batch = this.batchFilter();
		const query = this.searchQuery();
		return this.queue
			.lines()
			.filter(
				(line) =>
					(!customer || line.customer === customer) &&
					(!isr || line.isr === isr) &&
					(!day || submittedDay(line.submittedAt) === day) &&
					(!batch || String(line.batchId) === batch) &&
					lineMatchesQuery(line, query)
			);
	});

	public readonly visibleLines = computed(() => {
		const filter = this.statusFilter();
		const lines = this.baseLines();
		return filter.size
			? lines.filter((line) => filter.has(line.status))
			: lines;
	});

	public readonly statusCounts = computed(() => {
		const counts: Record<PartCodingStatus, number> = {
			Auto: 0,
			Manual: 0,
			Review: 0,
			'Out of Stock': 0,
			RFQ: 0
		};
		for (const line of this.baseLines()) {
			counts[line.status]++;
		}
		return counts;
	});

	// Only lines still awaiting a decision can be ticked.
	public readonly selectableLines = computed(() =>
		this.visibleLines().filter((line) => !line.approval)
	);
	public readonly selectedCount = computed(() => this.selectedIds().size);
	public readonly allSelected = computed(
		() =>
			this.selectableLines().length > 0 &&
			this.selectableLines().every((line) =>
				this.selectedIds().has(line.id)
			)
	);

	public readonly someSelected = computed(
		() =>
			!this.allSelected() &&
			this.selectableLines().some((line) =>
				this.selectedIds().has(line.id)
			)
	);

	public readonly showingLabel = computed(() => {
		const total = this.queue.lines().length;
		if (!total) return '';
		const showing = this.visibleLines().length;
		if (showing !== total) return `showing ${showing} of ${total}`;
		const customers = new Set(this.queue.lines().map((l) => l.customer))
			.size;
		return (
			`${total} line${total === 1 ? '' : 's'} across ` +
			`${customers} customer${customers === 1 ? '' : 's'}`
		);
	});

	public ngOnInit(): void {
		void this.queue.load();
	}

	public hasQueue(): boolean {
		return this.queue.lines().length > 0;
	}

	public setCustomerFilter(value: string | null): void {
		this.customerFilter.set(value);
		this.resetSelectionAndStatus();
	}

	public setIsrFilter(value: string | null): void {
		this.isrFilter.set(value);
		this.resetSelectionAndStatus();
	}

	public setDateFilter(value: string | null): void {
		this.dateFilter.set(value);
		this.resetSelectionAndStatus();
	}

	public setBatchFilter(value: string | null): void {
		this.batchFilter.set(value);
		this.resetSelectionAndStatus();
	}

	public setSearchQuery(value: string): void {
		this.searchQuery.set(value);
		this.selectedIds.set(new Set());
	}

	public toggleStatusFilter(status: PartCodingStatus): void {
		const next = new Set(this.statusFilter());
		if (next.has(status)) {
			next.delete(status);
		} else {
			next.add(status);
		}
		this.statusFilter.set(next);
		// Selection would otherwise point at rows that are now hidden.
		this.selectedIds.set(new Set());
	}

	public clearFilters(): void {
		this.customerFilter.set(null);
		this.isrFilter.set(null);
		this.dateFilter.set(null);
		this.searchQuery.set('');
		this.resetSelectionAndStatus();
	}

	public toggleSelected(id: string, checked: boolean): void {
		const next = new Set(this.selectedIds());
		if (checked) {
			next.add(id);
		} else {
			next.delete(id);
		}
		this.selectedIds.set(next);
	}

	public toggleAllSelected(checked: boolean): void {
		this.selectedIds.set(
			checked
				? new Set(this.selectableLines().map((line) => line.id))
				: new Set()
		);
	}

	public clearSelection(): void {
		this.selectedIds.set(new Set());
	}

	public async decideSelected(approval: PartCodingApproval): Promise<void> {
		const lines = this.selectableLines().filter((line) =>
			this.selectedIds().has(line.id)
		);
		if (!lines.length) {
			this.toastService.showErrorToast('Nothing selected.');
			return;
		}
		const customers = new Set(lines.map((line) => line.customer)).size;
		const history = this.queue.decide(
			new Set(lines.map((line) => line.id)),
			approval,
			this.accountName(),
			Date.now()
		);
		this.selectedIds.set(new Set());
		this.reasonPopover?.hide();
		this.toastService.showSuccessToast(
			`${this.decisionVerb(approval)} ${lines.length} line${lines.length === 1 ? '' : 's'}` +
				(customers > 1 ? ` across ${customers} customers` : '')
		);
		this.reportHistorySkips(await history);
	}

	public async decideLine(
		line: PartCodingQueueLine,
		approval: PartCodingApproval
	): Promise<void> {
		const history = this.queue.decide(
			new Set([line.id]),
			approval,
			this.accountName(),
			Date.now()
		);
		this.toggleSelected(line.id, false);
		this.reasonPopover?.hide();
		this.toastService.showSuccessToast(
			`${this.decisionVerb(approval)} ${line.customer} line ${line.mark} — ` +
				(line.status === 'RFQ' ? 'RFQ' : line.partCode)
		);
		this.reportHistorySkips(await history);
	}

	public openReasonPopover(event: Event, line: PartCodingQueueLine): void {
		this.activeLine.set(line);
		this.reasonPopover?.toggle(event);
	}

	public exportQueue(scope: 'all' | 'selected'): void {
		if (!this.hasQueue()) {
			this.toastService.showErrorToast(
				'Nothing to export — the queue is empty.'
			);
			return;
		}

		let lines: readonly PartCodingQueueLine[];
		if (scope === 'selected') {
			lines = this.queue
				.lines()
				.filter((line) => this.selectedIds().has(line.id));
			if (!lines.length) {
				this.toastService.showErrorToast(
					'Select one or more lines to export.'
				);
				return;
			}
		} else {
			lines = this.visibleLines();
			if (!lines.length) {
				this.toastService.showErrorToast(
					'No lines match the current filters to export.'
				);
				return;
			}
		}

		const csv = buildAdminQueueCsv(lines);
		// The BOM makes Excel read the file as UTF-8 instead of its legacy codepage.
		const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `admin_queue_${new Date().toISOString().slice(0, 10)}.csv`;
		anchor.click();
		URL.revokeObjectURL(url);

		this.toastService.showSuccessToast(
			`Exported ${lines.length} line${lines.length === 1 ? '' : 's'} — fill Approval ` +
				'with Approved or Rejected, then Upload. Only the Approval column is applied.'
		);
	}

	public startUpload(input: HTMLInputElement): void {
		if (!this.hasQueue()) {
			this.toastService.showErrorToast(
				'Nothing to update — the queue is empty.'
			);
			return;
		}
		input.click();
	}

	public async onUploadSelected(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (!file) return;

		const result = readApprovalUpload(
			await file.text(),
			this.queue.lines()
		);
		if (result.errors.length) {
			this.uploadFileName.set(file.name);
			this.uploadErrors.set(result.errors);
			this.uploadDialogVisible.set(true);
			return;
		}
		if (!result.decisions.size) {
			this.toastService.showSuccessToast(
				'Upload read fine — no Approval values to apply (only the Approval column is applied).'
			);
			return;
		}

		const decidedBy = this.accountName();
		const decidedAt = Date.now();
		const histories: Promise<readonly HistoryWriteOutcome[]>[] = [];
		for (const approval of ['approved', 'rejected'] as const) {
			const ids = new Set(
				[...result.decisions]
					.filter(([, value]) => value === approval)
					.map(([id]) => id)
			);
			if (ids.size)
				histories.push(
					this.queue.decide(ids, approval, decidedBy, decidedAt)
				);
		}
		this.selectedIds.set(new Set());
		this.reasonPopover?.hide();
		this.toastService.showSuccessToast(
			`Applied ${result.decisions.size} approval${result.decisions.size === 1 ? '' : 's'} from ${file.name} — only the Approval column is applied`
		);
		this.reportHistorySkips((await Promise.all(histories)).flat());
	}

	public initials(name: string): string {
		return name
			.split(/\s+/)
			.filter(Boolean)
			.map((word) => word[0])
			.join('')
			.slice(0, 2)
			.toUpperCase();
	}

	public statusSeverity(
		status: PartCodingStatus
	): 'success' | 'info' | 'warn' | 'danger' | 'contrast' {
		switch (status) {
			case 'Auto':
				return 'success';
			case 'Manual':
				return 'info';
			case 'Review':
				return 'warn';
			case 'Out of Stock':
				return 'contrast';
			case 'RFQ':
				return 'danger';
		}
	}

	public confidenceSeverity(
		confidence: number
	): 'success' | 'warn' | 'danger' {
		if (confidence >= 90) return 'success';
		if (confidence >= 70) return 'warn';
		return 'danger';
	}

	public approvalSeverity(
		approval: PartCodingApproval | null
	): 'success' | 'danger' | 'secondary' {
		switch (approval) {
			case 'approved':
				return 'success';
			case 'rejected':
				return 'danger';
			case null:
				return 'secondary';
		}
	}

	public approvalLabel(approval: PartCodingApproval | null): string {
		return approval ? this.decisionVerb(approval) : 'Pending';
	}

	public marketBasketSeverity(value: boolean): 'success' | 'danger' {
		return value ? 'success' : 'danger';
	}

	private resetSelectionAndStatus(): void {
		this.statusFilter.set(new Set());
		this.selectedIds.set(new Set());
	}

	private reportHistorySkips(outcomes: readonly HistoryWriteOutcome[]): void {
		const skipped = outcomes.filter((outcome) => outcome.skipReason);
		if (!skipped.length) return;
		const lines = skipped
			.map(
				({ line, skipReason }) =>
					`${line.customer} line ${line.mark}: ${skipReason ?? ''}`
			)
			.join('; ');
		this.toastService.showErrorToast(
			`Still approved, but ${skipped.length} line${skipped.length === 1 ? ' was' : 's were'} ` +
				`not added to history — ${lines}`,
			true
		);
	}

	private decisionVerb(approval: PartCodingApproval): string {
		return approval === 'approved' ? 'Approved' : 'Rejected';
	}

	private optionsFrom(values: readonly string[]): FilterOption[] {
		return [...new Set(values.filter(Boolean))]
			.sort((a, b) =>
				a.localeCompare(b, undefined, { sensitivity: 'base' })
			)
			.map((value) => ({ label: value, value }));
	}

	private accountName(): string {
		const account = this.msal.instance.getActiveAccount();
		return account?.name ?? account?.username ?? 'Signed in';
	}
}
